import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CAMPAIGN_LOGO = 'https://customer-assets.emergentagent.com/job_bba65e17-d01f-4e0d-ac4c-a997107c6926/artifacts/tvydclge_1D4B9B2B-E0EB-46B4-B318-D85B7D6F1D85.png';
const CONTACT_EMAIL = 'cliandaflorence@friendsofcliandaflorence.com';

const H2 = ({ children }) => (
  <h2 className="text-2xl font-bold text-purple-950 mt-10 mb-4 leading-snug">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-bold text-slate-900 mt-7 mb-3">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-base text-slate-700 leading-relaxed mb-4">{children}</p>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 mb-5 text-base text-slate-700 leading-relaxed">{children}</ul>
);
const Email = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 font-semibold underline hover:text-purple-900">
    {CONTACT_EMAIL}
  </a>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="privacy-policy-page">
      <header className="bg-purple-900 py-4 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 transition-transform duration-300 hover:scale-105" data-testid="legal-logo-home-link">
            <img src={CAMPAIGN_LOGO} alt="Clianda Florence Campaign Logo" className="h-12 md:h-14 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-white hover:text-purple-200 font-semibold text-sm uppercase tracking-wide transition-colors" data-testid="legal-back-home-link">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="container mx-auto px-6 max-w-3xl py-12 lg:py-20">
          <p className="text-purple-700 font-semibold uppercase tracking-wide text-sm mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-purple-950 leading-tight tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mb-10 pb-8 border-b border-slate-200">
            Last Updated: October 21, 2026
          </p>

          <P>
            This privacy notice for Friends of Clianda Florence (&ldquo;committee,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) explains how and why we may collect, store, use, and share
            information when you interact with our campaign, website, forms, communications, events, donation
            pages, volunteer opportunities, SMS/MMS text messaging program, email communications, or other
            campaign-related activities.
          </P>
          <P>This Privacy Policy applies when you:</P>
          <UL>
            <li>Visit our website or any website of ours that links to this Privacy Policy</li>
            <li>Register for events</li>
            <li>Sign up to receive campaign updates</li>
            <li>Volunteer or request more information</li>
            <li>Make a donation</li>
            <li>Engage with us through SMS/MMS text messages, email, forms, marketing, events, or other campaign communications</li>
          </UL>
          <P>
            Reading this Privacy Policy will help you understand how Friends of Clianda Florence collects, uses,
            and protects information. If you do not agree with this Privacy Policy, please do not use our website,
            forms, communications, or services.
          </P>
          <P>If you have questions or concerns, please contact us at <Email />.</P>

          <H2>Summary of Key Points</H2>
          <P>This summary provides key points from our Privacy Policy.</P>

          <H2>What Personal Information Do We Collect?</H2>
          <P>
            When you visit, use, or interact with our website, forms, events, donation pages, volunteer
            opportunities, SMS/MMS text messaging program, email communications, or other campaign services, we
            may collect personal information depending on how you interact with Friends of Clianda Florence.
          </P>

          <H3>Personal Information You Provide</H3>
          <P>The personal information we collect may include:</P>
          <UL>
            <li>Name</li>
            <li>Phone number</li>
            <li>Mobile phone number</li>
            <li>Email address</li>
            <li>Mailing address</li>
            <li>Event registration information</li>
            <li>Volunteer or supporter information</li>
            <li>Donation-related information</li>
            <li>Messages or responses you send to us</li>
          </UL>

          <H3>Technical, Device, and Security Information</H3>
          <P>
            When you access our website, we and the service providers that operate and protect it may
            automatically collect limited technical information. This information may include your Internet
            Protocol (IP) address; request date and time; pages, files, and links requested; referring website or
            domain; browser type and version; operating system; device type; user-agent string; preferred
            language; time zone; screen or viewport dimensions when available; approximate geographic region
            derived from an IP address; network or autonomous system number (ASN); connection protocol; response
            status; security-event information; bot or automated-traffic indicators; and similar diagnostic
            metadata.
          </P>
          <P>
            We collect and use this technical information for website and network security, fraud and abuse
            prevention, incident investigation, troubleshooting, performance monitoring, and ensuring that website
            content and features operate correctly across browsers, operating systems, screen sizes, networks, and
            devices. This technical information is not collected for the purpose of determining a visitor&rsquo;s
            real-world identity solely from ordinary browsing activity.
          </P>
          <P>
            We use Optiryte as a technical service provider for IP-related lookup, security, and compatibility
            functions. When a page loads, the website may send the full page URL to Optiryte and receive
            IP-related lookup information in response. Optiryte may process the visitor&rsquo;s IP address and
            related request metadata as necessary to provide this service. We use this information for the
            security, troubleshooting, diagnostic, and compatibility purposes described above.
          </P>

          <H3>SMS/MMS Text Messaging</H3>
          <P>
            If you provide your mobile phone number and opt in to receive text messages, you agree to receive
            recurring SMS/MMS text messages from Friends of Clianda Florence. Messages may include campaign alerts,
            donation requests, updates, voter outreach, event invitations, polling questions, get-out-the-vote
            reminders, election information, and other important campaign information.
          </P>
          <P>Message frequency varies. Msg &amp; data rates may apply. Reply HELP for help. Reply STOP to opt out at any time.</P>
          <P>
            Consent to receive SMS/MMS text messages is not a condition of making a donation, volunteering,
            supporting the campaign, or using this website.
          </P>
          <P>
            Friends of Clianda Florence does not sell, rent, or share mobile opt-in data or SMS consent with third
            parties for their own marketing purposes. Mobile phone numbers and SMS consent information may be
            shared with service providers only as necessary to operate campaign communications, comply with law,
            or support campaign operations.
          </P>

          <H2>How We Use Your Information</H2>
          <P>Friends of Clianda Florence may use the information we collect to:</P>
          <UL>
            <li>Communicate with voters, supporters, volunteers, donors, and community members</li>
            <li>Send campaign updates, voter outreach, event invitations, volunteer opportunities, fundraising reminders, polling questions, get-out-the-vote reminders, election information, and public service announcements</li>
            <li>Respond to questions, requests, replies, or other communications</li>
            <li>Process event registrations, volunteer sign-ups, supporter forms, and donations</li>
            <li>Improve our website, forms, communications, and campaign operations</li>
            <li>Maintain website security and investigate malicious, fraudulent, abusive, or automated activity</li>
            <li>Diagnose errors and ensure compatibility across browsers, operating systems, screen sizes, networks, and devices</li>
            <li>Comply with legal, reporting, regulatory, security, and campaign-related obligations</li>
            <li>Protect against misuse, fraud, unauthorized activity, or security incidents</li>
          </UL>

          <H2>How We Share Information</H2>
          <P>Friends of Clianda Florence does not sell personal information.</P>
          <P>
            We may share information with service providers, vendors, consultants, or other partners who help us
            operate the campaign, website, forms, SMS/MMS messaging, email communications, donation processing,
            event registration, data management, compliance, security, or other campaign operations.
          </P>
          <P>
            These service providers may only use information as needed to provide services to Friends of Clianda
            Florence, comply with law, or support campaign operations.
          </P>
          <P>
            Mobile opt-in data and SMS consent are not sold, rented, or shared with third parties for their own
            marketing purposes.
          </P>

          <H2>Do We Process Sensitive Personal Information?</H2>
          <P>
            We may process sensitive personal information only when necessary with your consent or as otherwise
            permitted by applicable law.
          </P>

          <H2>Do We Receive Information From Third Parties?</H2>
          <P>
            We may receive information from public databases, voter files, public records, marketing partners,
            social media platforms, service providers, and other lawful sources.
          </P>

          <H2>How Do We Keep Your Information Safe?</H2>
          <P>
            We use reasonable organizational, technical, and administrative measures designed to protect personal
            information. However, no electronic transmission over the internet or information storage system can be
            guaranteed to be 100% secure. We cannot promise or guarantee that unauthorized third parties will never
            be able to defeat our security measures or improperly collect, access, steal, or modify information.
          </P>

          <H2>Technical Log Retention</H2>
          <P>
            Technical and security logs may be retained for as long as reasonably necessary to protect the
            website, investigate security incidents, diagnose compatibility or performance problems, maintain
            reliable operations, comply with legal obligations, and resolve disputes. Records may be reviewed and
            deleted manually when they are no longer needed for these purposes. Retention periods may vary
            according to the nature of the record, the risk being investigated, and the systems used by our
            service providers.
          </P>

          <H2>Your Choices</H2>
          <P>
            You may opt out of SMS/MMS text messages at any time by replying STOP to any text message from Friends
            of Clianda Florence. You may reply HELP for help.
          </P>
          <P>You may also contact us at <Email /> with questions or requests regarding your information.</P>

          <H2>Your Rights</H2>
          <P>
            Depending on where you are located, applicable law may provide certain rights regarding your personal
            information. These rights may include requesting access to, correction of, deletion of, or information
            about the personal information we maintain about you.
          </P>
          <P>
            To exercise privacy-related rights or make a request, contact us at <Email />. We will review and
            respond to requests in accordance with applicable law.
          </P>

          <H2>Third-Party Links</H2>
          <P>
            Our website, text messages, emails, or other campaign communications may include links to third-party
            websites, donation platforms, event pages, social media platforms, voter information resources, or
            other websites. Friends of Clianda Florence is not responsible for the privacy practices, content, or
            policies of third-party websites.
          </P>

          <H2>Changes to This Privacy Policy</H2>
          <P>
            We may update this Privacy Policy from time to time. Any changes will be posted on this page with an
            updated &ldquo;Last Updated&rdquo; date. Changes and clarifications take effect when posted.
          </P>

          <H2>Contact Us</H2>
          <P>
            If you have questions about this Privacy Policy or would like to access, correct, amend, or delete
            personal information you have provided, contact Friends of Clianda Florence at: <Email />
          </P>
          <P>
            See also our{' '}
            <Link to="/terms-and-conditions/" className="text-purple-700 font-semibold underline hover:text-purple-900" data-testid="privacy-terms-link">
              Terms &amp; Conditions
            </Link>
            .
          </P>

          <p className="text-base font-bold text-purple-950 mt-14 pt-8 border-t border-slate-200">
            Paid for by Friends of Clianda Florence.
          </p>
        </div>
      </main>

      <footer className="bg-slate-900 py-8 text-center">
        <Link to="/" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="legal-footer-home-link">
          Return to friendsofcliandaflorence.com
        </Link>
        <p className="text-xs text-slate-500 mt-3">
          © {new Date().getFullYear()} Friends of Clianda Florence. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
